import { isExcelReadOnlyField } from "@/excel/excelReadOnlyField";
import { walkSchemaForRows } from "@/excel/speckle/fieldWalker";
import {
  RECEIVE_LAYOUT_CONFIG,
  type ExcelCellValue,
  type ReceiveFieldRow,
  type ReceiveValidationKind,
} from "@/excel/speckle/receiveTypes";
import {
  getSchemaDescription,
  getSchemaEnumValues,
  getSchemaPrimitiveType,
  getSchemaTitle,
  getSchemaUnitLabel,
  resolveSchemaAtPath,
  resolveWorkbookSchemaForProfile,
} from "@/services/equation-excel-client-metadata/equationExcelWorkbookSchemaResolver";
import type {
  ExtendedDocument,
  ResolvedExtendedSchemaObject,
  VisibleExcelProfile,
} from "@/services/open-api/openApi.types";
import type { SpeckleReceiveResponse } from "@/services/speckle/speckleExcelApi";

type BasicReceiveValidation = {
  kind: ReceiveValidationKind;
  allowedValues: string[];
  minimum?: number;
  maximum?: number;
  message: string;
};

function hasSchemaUnitMetadata(schema: ResolvedExtendedSchemaObject | null | undefined): boolean {
  return Boolean(schema?.unit);
}

function getSchemaNumberValue(
  schema: ResolvedExtendedSchemaObject | null | undefined,
  key: "minimum" | "maximum"
): number | undefined {
  const value = schema?.[key];

  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function normalizeVisibleBooleanValue(value: ExcelCellValue): ExcelCellValue {
  if (value === true) {
    return "TRUE";
  }

  if (value === false) {
    return "FALSE";
  }

  return value;
}

function formatPathSegment(segment: string): string {
  return segment
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function createFieldLabelFromPath(path: string): string {
  const parts = path.split(".").filter(Boolean);

  return parts.length > 0 ? formatPathSegment(parts[parts.length - 1]) : "";
}

function getFieldStatus(editable: boolean, type: string): string {
  if (!editable) {
    return "Read-only";
  }

  if (type === "json") {
    return "Advanced";
  }

  return "";
}

function createBasicReceiveValidation(
  type: string,
  editable: boolean,
  apiSchema?: ExtendedDocument,
  schema?: ResolvedExtendedSchemaObject | null
): BasicReceiveValidation {
  if (!editable) {
    return {
      kind: "none",
      allowedValues: [],
      message: "This value is read-only. Save will reject changes to this value.",
    };
  }

  const enumValues = apiSchema && schema ? getSchemaEnumValues(apiSchema, schema) : [];

  if (enumValues.length > 0) {
    return {
      kind: "enum",
      allowedValues: enumValues,
      message: "Choose one of the allowed values.",
    };
  }

  const primitiveType = apiSchema && schema ? getSchemaPrimitiveType(apiSchema, schema) : undefined;

  if (primitiveType === "boolean" || type === "boolean") {
    return {
      kind: "boolean",
      allowedValues: ["TRUE", "FALSE"],
      message: "Choose TRUE or FALSE.",
    };
  }

  if (primitiveType === "number" || primitiveType === "integer" || type === "number") {
    const shouldUseSchemaMinMax = !hasSchemaUnitMetadata(schema);

    return {
      kind: "number",
      allowedValues: [],
      minimum: shouldUseSchemaMinMax ? getSchemaNumberValue(schema, "minimum") : undefined,
      maximum: shouldUseSchemaMinMax ? getSchemaNumberValue(schema, "maximum") : undefined,
      message: "Enter a number.",
    };
  }

  return {
    kind: "none",
    allowedValues: [],
    message: "",
  };
}

export function createReceiveFieldRows(
  response: SpeckleReceiveResponse,
  excelProfile: VisibleExcelProfile,
  apiSchema?: ExtendedDocument
): ReceiveFieldRow[] {
  const rootSchema = apiSchema ? resolveWorkbookSchemaForProfile(apiSchema, excelProfile) : null;
  const flattenedRows = walkSchemaForRows(response.obj, "", apiSchema, rootSchema);

  return flattenedRows.map((row, index) => {
    const schema = apiSchema && rootSchema ? resolveSchemaAtPath(apiSchema, rootSchema, row.path, response.obj) : null;
    const editable = !isExcelReadOnlyField(row.path, schema);
    const validation = createBasicReceiveValidation(row.type, editable, apiSchema, schema);

    return {
      rowId: index + 1,
      path: row.path,
      value: row.value,
      type: row.type,
      originalValue: row.value,
      editable,
      field: getSchemaTitle(schema) ?? createFieldLabelFromPath(row.path),
      unit: getSchemaUnitLabel(schema),
      status: getFieldStatus(editable, row.type),
      description: getSchemaDescription(schema) ?? "",
      validationKind: validation.kind,
      allowedValues: validation.allowedValues,
      minimum: validation.minimum,
      maximum: validation.maximum,
      validationMessage:
        validation.kind === "enum" && validation.message === ""
          ? "Choose one of the allowed values."
          : validation.message,
    };
  });
}

export function createReceiveTableRows(fieldRows: ReceiveFieldRow[]): ExcelCellValue[][] {
  return [
    [...RECEIVE_LAYOUT_CONFIG.headers],
    ...fieldRows.map((row) => [
      row.rowId,
      row.path,
      row.field,
      normalizeVisibleBooleanValue(row.value),
      row.unit,
      row.status,
      row.description,
    ]),
  ];
}
